import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { contributionsService, type Contribution } from '../lib/contributions';
import { curationsService } from '../lib/curations';

interface ContributionsCache {
  contributions: Contribution[];
  userContributions: Contribution[];
  savedContributions: Contribution[];
  lastFetchTime: number | null;
  searchQuery: string;
  lastContributionTimestamp: string | null; // Track most recent contribution
  // Pagination state
  offset: number;
  hasMore: boolean;
  totalCount: number;
  pageSize: number;
}

interface ContributionsContextType {
  cache: ContributionsCache;
  isLoading: boolean;
  isFetchingMore: boolean;
  error: string | null;
  fetchContributions: (userId: string, force?: boolean) => Promise<void>;
  fetchNextPage: (userId: string) => Promise<void>; // Load next page
  checkForUpdates: () => Promise<boolean>; // Check if new content exists
  updateContribution: (contribution: Contribution) => void;
  addContribution: (contribution: Contribution) => void;
  deleteContribution: (contributionId: string) => void;
  toggleSave: (contributionId: string, userId: string, isSaved: boolean) => Promise<void>;
  setSearchQuery: (query: string) => void;
  clearCache: () => void;
}

// Cache persists for entire session (no expiration)

const ContributionsContext = createContext<ContributionsContextType | undefined>(undefined);

const PAGE_SIZE = 30; // Number of items per page

export function ContributionsProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = useState<ContributionsCache>({
    contributions: [],
    userContributions: [],
    savedContributions: [],
    lastFetchTime: null,
    searchQuery: '',
    lastContributionTimestamp: null,
    offset: 0,
    hasMore: true,
    totalCount: 0,
    pageSize: PAGE_SIZE
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  // Check if new contributions exist without fetching all data
  const checkForUpdates = useCallback(async () => {
    try {
      // Quick query to get the most recent contribution timestamp
      const { data, error } = await contributionsService.supabase
        .from('contributions')
        .select('created_at')
        .eq('is_active', true)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('Error checking for updates:', error);
        return false;
      }

      // If no cached timestamp or if there's a newer contribution, return true
      if (!cache.lastContributionTimestamp || !data) {
        return cache.contributions.length === 0; // Only true if cache is empty
      }

      const latestTimestamp = new Date(data.created_at).getTime();
      const cachedTimestamp = new Date(cache.lastContributionTimestamp).getTime();

      const hasUpdates = latestTimestamp > cachedTimestamp;
      if (hasUpdates) {
        console.log('New contributions detected');
      }

      return hasUpdates;
    } catch (error) {
      console.error('Error checking for updates:', error);
      return false;
    }
  }, [cache.lastContributionTimestamp, cache.contributions.length]);

  const fetchContributions = useCallback(async (userId: string, force = false) => {
    // Always fetch if cache is empty, otherwise use cached data unless forced
    if (!force && cache.contributions.length > 0) {
      console.log('Using cached contributions data');
      return;
    }

    // If already fetching, return the existing promise
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    const fetchPromise = (async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Fetching first page of contributions data');

        // Fetch first page of contributions with likes using pagination
        const result = await contributionsService.getContributionsWithSavesPaginated(0, PAGE_SIZE);

        // Fetch the user's own contributions directly rather than deriving
        // them from the feed page: the feed excludes hidden items (and only
        // covers the first page), but the owner's profile must always show
        // everything they own (PUR-19).
        const [userContribs, saved] = await Promise.all([
          contributionsService.getUserContributions(userId),
          curationsService.getUserSavedContributions(userId),
        ]);

        // Get the most recent contribution timestamp
        const mostRecent = result.data.length > 0
          ? result.data[0].created_at
          : null;

        setCache({
          contributions: result.data,
          userContributions: userContribs,
          savedContributions: saved || [],
          lastFetchTime: Date.now(),
          searchQuery: cache.searchQuery,
          lastContributionTimestamp: mostRecent,
          offset: result.data.length,
          hasMore: result.hasMore,
          totalCount: result.count,
          pageSize: PAGE_SIZE
        });
      } catch (err) {
        console.error('Error fetching contributions:', err);
        setError('Failed to load contributions');
      } finally {
        setIsLoading(false);
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [cache.contributions.length, cache.searchQuery]);

  const fetchNextPage = useCallback(async (userId: string) => {
    // Don't fetch if already loading or no more items
    if (isFetchingMore || !cache.hasMore) {
      return;
    }

    try {
      setIsFetchingMore(true);
      setError(null);

      console.log(`Fetching next page: offset=${cache.offset}`);

      // Fetch next page
      const result = await contributionsService.getContributionsWithSavesPaginated(cache.offset, PAGE_SIZE);

      if (result.data.length === 0) {
        setCache(prev => ({ ...prev, hasMore: false }));
        return;
      }

      // userContributions is fetched directly in fetchContributions, not
      // derived from feed pages — nothing to append here.
      setCache(prev => ({
        ...prev,
        contributions: [...prev.contributions, ...result.data],
        offset: prev.offset + result.data.length,
        hasMore: result.hasMore,
        totalCount: result.count
      }));
    } catch (err) {
      console.error('Error fetching next page:', err);
      setError('Failed to load more contributions');
    } finally {
      setIsFetchingMore(false);
    }
  }, [cache.offset, cache.hasMore, isFetchingMore]);

  const updateContribution = useCallback((updatedContribution: Contribution) => {
    setCache(prev => ({
      ...prev,
      contributions: prev.contributions.map(c =>
        c.id === updatedContribution.id ? updatedContribution : c
      ),
      userContributions: prev.userContributions.map(c =>
        c.id === updatedContribution.id ? updatedContribution : c
      ),
      savedContributions: prev.savedContributions.map(c =>
        c.id === updatedContribution.id ? updatedContribution : c
      )
    }));
  }, []);

  const addContribution = useCallback((contribution: Contribution) => {
    setCache(prev => ({
      ...prev,
      contributions: [contribution, ...prev.contributions],
      userContributions: contribution.user_id === prev.userContributions[0]?.user_id
        ? [contribution, ...prev.userContributions]
        : prev.userContributions,
      lastFetchTime: Date.now(),
      lastContributionTimestamp: contribution.created_at || new Date().toISOString()
    }));
  }, []);

  const deleteContribution = useCallback((contributionId: string) => {
    setCache(prev => ({
      ...prev,
      contributions: prev.contributions.filter(c => c.id !== contributionId),
      userContributions: prev.userContributions.filter(c => c.id !== contributionId),
      savedContributions: prev.savedContributions.filter(c => c.id !== contributionId),
      lastFetchTime: Date.now()
    }));
  }, []);

  const toggleSave = useCallback(async (contributionId: string, userId: string, isSaved: boolean) => {
    try {
      await curationsService.toggleSave(userId, contributionId);

      // Update local cache
      setCache(prev => {
        const updatedContributions = prev.contributions.map(c =>
          c.id === contributionId
            ? { ...c, is_saved: !isSaved, saves_count: (c.saves_count || 0) + (isSaved ? -1 : 1) }
            : c
        );

        const updatedUserContributions = prev.userContributions.map(c =>
          c.id === contributionId
            ? { ...c, is_saved: !isSaved, saves_count: (c.saves_count || 0) + (isSaved ? -1 : 1) }
            : c
        );

        let updatedSavedContributions = prev.savedContributions;
        if (!isSaved) {
          // Adding to saved
          const item = updatedContributions.find(c => c.id === contributionId);
          if (item && !prev.savedContributions.find(c => c.id === contributionId)) {
            updatedSavedContributions = [...prev.savedContributions, { ...item, is_saved: true }];
          }
        } else {
          // Removing from saved
          updatedSavedContributions = prev.savedContributions.filter(c => c.id !== contributionId);
        }

        return {
          ...prev,
          contributions: updatedContributions,
          userContributions: updatedUserContributions,
          savedContributions: updatedSavedContributions
        };
      });
    } catch (err) {
      console.error('Error toggling save:', err);
      throw err;
    }
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setCache(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const clearCache = useCallback(() => {
    setCache({
      contributions: [],
      userContributions: [],
      savedContributions: [],
      lastFetchTime: null,
      searchQuery: '',
      lastContributionTimestamp: null,
      offset: 0,
      hasMore: true,
      totalCount: 0,
      pageSize: PAGE_SIZE
    });
  }, []);

  // No automatic cache clearing - cache persists for entire session

  return (
    <ContributionsContext.Provider value={{
      cache,
      isLoading,
      isFetchingMore,
      error,
      fetchContributions,
      fetchNextPage,
      checkForUpdates,
      updateContribution,
      addContribution,
      deleteContribution,
      toggleSave,
      setSearchQuery,
      clearCache
    }}>
      {children}
    </ContributionsContext.Provider>
  );
}

export function useContributions() {
  const context = useContext(ContributionsContext);
  if (!context) {
    throw new Error('useContributions must be used within ContributionsProvider');
  }
  return context;
}